/**
 * VaultStack Domain Router
 * Loads business config based on hostname for multi-tenant support
 * 
 * Architecture:
 * - vaultstack-sbp.netlify.app → Platform home (generic branding)
 * - joes.vaultstack-sbp.app → Joe's Mowing (custom branding)
 * - joesmowing.com → Joe's Mowing (custom domain)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig, type BusinessConfig } from '@/hooks/useBusinessConfig';

interface DomainConfig {
  config: BusinessConfig | null;
  loading: boolean;
  isPlatform: boolean; // true if on main vaultstack-sbp.netlify.app
  subdomain: string | null;
}

export function useDomainRouter(): DomainConfig {
  const { config: businessConfig } = useBusinessConfig(); // ← hoisted here
  const [domainConfig, setDomainConfig] = useState<DomainConfig>({
    config: null,
    loading: true,
    isPlatform: true,
    subdomain: null,
  });

  useEffect(() => {
    async function loadDomainConfig() {
      try {
        const hostname = window.location.hostname;
        
        const isPlatformDomain = hostname.includes('vaultstack-sbp.netlify.app') || 
                                 hostname === 'localhost' ||
                                 hostname === '127.0.0.1';

        if (isPlatformDomain) {
          setDomainConfig({
            config: businessConfig || null,  // ← use hoisted value
            loading: false,
            isPlatform: true,
            subdomain: null,
          });
          return;
        }

        const parts = hostname.split('.');
        const subdomain = parts.length > 2 ? parts[0] : null;

        const { data: config, error } = await supabase
          .from('business_config')
          .select('*')
          .eq('domain', hostname)
          .maybeSingle();

        if (error) {
          console.error('Domain config error:', error);
        }

        setDomainConfig({
          config: config || null,
          loading: false,
          isPlatform: false,
          subdomain: subdomain,
        });
      } catch (error) {
        console.error('Domain router error:', error);
        setDomainConfig({
          config: null,
          loading: false,
          isPlatform: true,
          subdomain: null,
        });
      }
    }

    loadDomainConfig();
  }, [businessConfig]); // ← add businessConfig as dependency

  return domainConfig;
}

/**
 * Get branded content based on domain config
 */
export function useBranding(domainConfig: DomainConfig) {
  const { config, isPlatform } = domainConfig;

  if (isPlatform) {
    // Platform branding
    return {
      appName: 'VaultStack Small Business Platform',
      tagline: 'Professional Business Management for Solo Operators',
      logo: '/vaultstack-logo.png',
      accentColor: '#1a1a1a', // Dark professional
      footerBranding: 'Powered by VaultStack',
    };
  }

  if (config) {
    // Customer branding
    return {
      appName: config.biz_name,
      tagline: `${config.biz_type === 'mowing' ? 'Professional' : 'Quality'} ${config.biz_type === 'mowing' ? 'Lawn Care' : 'Services'}`,
      logo: config.logo_url || '/default-logo.png',
      accentColor: '#6B46C0', // Purple (can be customized per customer later)
      footerBranding: `© ${new Date().getFullYear()} ${config.biz_name}`,
    };
  }

  // Fallback branding
  return {
    appName: 'VaultStack',
    tagline: 'Business Management Platform',
    logo: '/vaultstack-logo.png',
    accentColor: '#1a1a1a',
    footerBranding: 'Powered by VaultStack',
  };
}
